import { api, unwrap } from "../lib/axios";
import type { ApiEnvelope } from "../types/api";
import type {
  BookingConfig,
  DebtLimitImpact,
  DebtLimitSyncResult,
  SafetyConfig,
  Settings,
} from "../types/domain";

export const settingsApi = {
  async get(): Promise<Settings> {
    const res = await api.get<ApiEnvelope<Settings>>("/settings");
    return unwrap(res);
  },

  /**
   * Partial update of the booking config. Only the keys sent are changed.
   *
   * NOTE: this used to silently discard vatPercentage / the gateway fees /
   * maxDriverDebt server-side. Always re-read after saving to confirm.
   */
  async updateBookingConfig(
    body: Partial<BookingConfig>,
  ): Promise<Settings> {
    const res = await api.patch<ApiEnvelope<Settings>>(
      "/settings/booking-config",
      body,
    );
    return unwrap(res);
  },

  /** Who a global debt ceiling of `maxDriverDebt` would block / unblock. Read-only. */
  async debtLimitImpact(maxDriverDebt: number): Promise<DebtLimitImpact> {
    const res = await api.get<ApiEnvelope<DebtLimitImpact>>(
      "/settings/debt-limit/impact",
      { params: { maxDriverDebt } },
    );
    return unwrap(res);
  },

  /** Re-check every driver whose trip block disagrees with the saved ceiling. */
  async syncDebtLimits(): Promise<DebtLimitSyncResult> {
    const res = await api.post<ApiEnvelope<DebtLimitSyncResult>>(
      "/settings/debt-limit/sync",
    );
    return unwrap(res);
  },

  /**
   * Partial update of the safety config. Send policeEmergencyNumber as an
   * empty string to clear it — omitting the key leaves it unchanged.
   */
  async updateSafetyConfig(body: Partial<SafetyConfig>): Promise<Settings> {
    const res = await api.patch<ApiEnvelope<Settings>>(
      "/settings/safety-config",
      body,
    );
    return unwrap(res);
  },
};
